
import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useInvoiceBatchProcess } from '@/hooks/useInvoice';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent,
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';

const InvoiceBatchProcess: React.FC = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1 + '');
  const [year, setYear] = useState(new Date().getFullYear() + '');
  const [testMode, setTestMode] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Invoice batch process hook
  const { processBatch: generateInvoices, resetBatch: resetGenerateBatch } = useInvoiceBatchProcess();

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  
  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const handleGenerateInvoices = async () => {
    try {
      setIsSubmitting(true);
      const selectedMonth = parseInt(month);
      const selectedYear = parseInt(year);
      
      console.log('Generating invoices with:', { 
        month: selectedMonth, 
        year: selectedYear, 
        testMode 
      });
      
      // Call the processBatch function
      const result = await generateInvoices({
        month: selectedMonth,
        year: selectedYear,
        testMode
      });
      
      console.log('Result:', result);
      
      // If we got here, the request was successful
      setIsSuccess(true);
      toast.success('Invoice batch has been initiated successfully');
      
    } catch (err) {
      console.error('Error generating invoices:', err);
      toast.error('An error occurred while initiating invoice generation.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmBatchProcess = () => {
    setShowConfirmation(true);
  };

  const resetForm = () => {
    setIsSuccess(false);
    resetGenerateBatch();
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Invoice Generation
        </CardTitle>
        <CardDescription>
          Generate invoices for subscription billing periods
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {!isSuccess ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="month">Month</Label>
                <Select value={month} onValueChange={setMonth} disabled={isSubmitting}>
                  <SelectTrigger id="month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Select value={year} onValueChange={setYear} disabled={isSubmitting}>
                  <SelectTrigger id="year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="test-mode"
                checked={testMode}
                onCheckedChange={setTestMode}
                disabled={isSubmitting}
              />
              <Label htmlFor="test-mode">Test mode (creates draft invoices for review)</Label>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>This process will:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Identify all subscription services due for billing in {monthOptions.find(m => m.value === month)?.label} {year}</li>
                <li>Calculate prorated amounts for services activated or terminated mid-month</li>
                <li>
                  {testMode
                    ? 'Create draft invoices that can be reviewed before finalizing'
                    : 'Create final invoices ready for distribution'}
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="text-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 font-medium text-lg">Batch Processing Initiated</h3>
              <p className="text-sm text-muted-foreground">
                Your invoice generation request for {monthOptions.find(m => m.value === month)?.label} {year} has been submitted successfully.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                The invoices are being processed in the background. You can check the batch history tab for status updates.
              </p>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter className={cn(
        "flex justify-between",
        isSubmitting && "opacity-50 pointer-events-none"
      )}>
        {!isSuccess ? (
          <Button onClick={confirmBatchProcess} disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : "Process Batch"}
          </Button>
        ) : (
          <Button className="w-full" onClick={resetForm}>
            Start New Batch
          </Button>
        )}
      </CardFooter>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Invoice Batch Processing</AlertDialogTitle>
            <AlertDialogDescription>
              {testMode ? (
                <span>You are about to generate <strong>TEST</strong> invoices for {monthOptions.find(m => m.value === month)?.label} {year}. This will create draft invoices that can be reviewed before finalizing.</span>
              ) : (
                <span>You are about to generate <strong>LIVE</strong> invoices for {monthOptions.find(m => m.value === month)?.label} {year}. This will create final invoices ready for distribution.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerateInvoices}>
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default InvoiceBatchProcess;
